import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { AbstractPort, Port } from '../fields/AbstractPort';

import { synchronize, synchronizePowerFlowDirection } from '../helpers';

import { environmentalWithCoolingMethod } from '../component-sections/environmental';

import { ComponentDefinition } from '../../types/ComponentDefinition';

const extraSpecifications = {};

const port = AbstractPort.extend({
    AC: Port.AC.extend({
        ...extraSpecifications,
    }).default({}),
    DC: Port.DC.extend({
        ...extraSpecifications,
    }).default({}),
});

const electrical = z
    .object({
        ports: z
            .tuple([port.default({ powerFlowDirection: 'input' }), port.default({ powerFlowDirection: 'output' })])
            .default([undefined, undefined]),
        standards: z.array(z.string()).default([]),
    })
    .default({});

const RapidShutdownDeviceBase = z.object({
    type: z.literal('rapidShutdownDevice').default('rapidShutdownDevice'),
    canServeAs: z.array(ComponentValidator.shape.type).default([]),
    electrical: synchronize(synchronizePowerFlowDirection(electrical), {
        'ports.0': ['ports.1'],
    }),
    environmental: environmentalWithCoolingMethod,
});

export const RapidShutdownDeviceValidator = ComponentValidator.merge(RapidShutdownDeviceBase);

export const RapidShutdownDevicePayloadValidator = ComponentPayloadValidator.merge(RapidShutdownDeviceBase);

export const RapidShutdownDefinition: ComponentDefinition = {
    type: 'rapidShutdownDevice',
    name: 'Rapid Shutdown Device',
    plural: 'Rapid Shutdown Devices',
    indicator: 'RSD',
    description:
        'A rapid shutdown device is a device that is used to rapidly shut down the flow of electricity from a photovoltaic system to ensure the safety of first responders.',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: false,
    ports: {
        min: 2,
        max: 2,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
        displayAsSinglePort: true,
    },
    canServeAsOptions: ['contactor', 'disconnect'],
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: RapidShutdownDeviceValidator,
        payload: RapidShutdownDevicePayloadValidator,
    },
};

export type RapidShutdownDevice = z.infer<typeof RapidShutdownDeviceValidator>;
export type RapidShutdownDevicePayload = z.infer<typeof RapidShutdownDevicePayloadValidator>;
